import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import PartnerPlaceholder from './PartnerPlaceholder';
import type { Partner } from '@/hooks/usePartners';

interface PartnerCardProps {
  partner: Partner;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ partner }) => {
  const [imageError, setImageError] = useState(false);

  const CardContent = () => (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
      <div className="p-6 text-center space-y-4 h-full flex flex-col">
        {/* Logo Container - Only show if logo_url exists */}
        {partner.logo_url && (
          <div className="flex justify-center">
            <div className="w-56 h-24 sm:w-64 sm:h-28 md:w-72 md:h-32 lg:w-80 lg:h-36 flex items-center justify-center">
              {!imageError ? (
                <img 
                  src={partner.logo_url}
                  alt={`${partner.name} Logo`}
                  className="w-full h-full object-contain"
                  onError={() => {
                    console.error('Failed to load partner logo:', {
                      name: partner.name,
                      url: partner.logo_url,
                      category: partner.category
                    });
                    setImageError(true);
                  }}
                />
              ) : (
                <PartnerPlaceholder name={partner.name} className="w-full h-full" />
              )}
            </div>
          </div>
        )}
        
        {/* Partner Info */}
        <div className="flex-1 space-y-2 flex flex-col justify-center">
          <h3 className="font-bold text-primary leading-tight text-xl">
            {partner.name}
          </h3>
          {/* Show address if no link available */}
          {!partner.link && partner.address && (
            <p className="text-base text-muted-foreground text-center">
              {partner.address}
            </p>
          )}
        </div>
        
        {/* Website Button or No Website Text - Only show if there's a link or no address */}
        {partner.link && (
          <div className="mt-auto">
            <a
              href={partner.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary text-primary bg-white hover:bg-primary hover:text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Website besuchen
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
        
        {/* Only show "keine Website verfügbar" if no link AND no address */}
        {!partner.link && !partner.address && (
          <div className="mt-auto">
            <p className="text-sm text-muted-foreground italic py-3">
              keine Website verfügbar
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return <CardContent />;
};

export default PartnerCard;